import styled from 'styled-components';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Form } from '@ant-design/compatible';
import { Col, Input } from 'antd';
import { FormItem, InputNumber, Button, Flex, FormText, Row } from 'ui';
import { injectIntl, FormattedMessage } from 'react-intl';
import ReferenceDataDropdown from
  'common/reference-data/containers/reference-data-dropdown-container';
import SearchInput from '../../../search/components/search-input';

const StyledChangeWarning = styled.div`
  color: ${p => p.theme.colors.theme}
  font-size: 16px;
  white-space: nowrap;
`;

const StyledInputNumber = styled(InputNumber)`
  border-color: ${p => p.isVendorCurrencyChanged && p.theme.colors.theme} !important;
  width: 100%
`;

const StyledClear = styled.div`
  text-decoration: underline;
  color: ${p => p.theme.colors.theme};
  cursor: pointer;
  margin-left: 25px;
  line-height: 2.5;
`;

const StyledButton = styled(Button)`
  ${p => p.disabled && 'cursor: not-allowed'}
`;

const SubcontractFooter = ({
  intl,
  addSubContract,
  vendors,
  form,
  disabled,
  setFormDirty,
  ...rest
}) => {
  const { getFieldDecorator, getFieldValue } = form;
  const [previousVendorCurrency, setPreviousVendorCurrency] = useState(null);
  const [isVendorCurrencyChanged, setIsVendorCurrencyChanged] = useState(false);

  const onChangeAuto = () => {
    if (
      getFieldValue('cost') &&
      previousVendorCurrency &&
      getFieldValue('costCurrency') !== previousVendorCurrency
    ) {
      setIsVendorCurrencyChanged(true);
    }
    setPreviousVendorCurrency(getFieldValue('costCurrency'));
  };

  const addLine = () => {
    form.validateFields((err, values) => {
      if (!err) {
        addSubContract(values);
        form.setFieldsValue({ vendor: null });
        form.resetFields();
        setIsVendorCurrencyChanged(false);
      }
    });
  };

  const onSubmit = () => {
    setFormDirty();
    addLine();
  };

  const isInvalid = (fieldsError, fieldsValue) =>
    Object.keys(fieldsValue).some(field => !fieldsValue[field]) ||
    Object.keys(fieldsError).some(field => fieldsError[field]);

  const handleSelect = (value) => {
    const vendor = vendors?.find(v => v.number === value);
    form.setFieldsValue({
      vendor: value,
      costCurrency: vendor?.currency,
      vendorName: vendor?.name,
    });
    return value;
  };

  const handleVendorClear = () => {
    form.setFieldsValue({
      vendor: null,
      costCurrency: null,
      vendorName: null,
    });
  };

  const typeValidator = getFieldDecorator('itemType', {
    initialValue: null,
    rules: [{ required: true, message: intl.formatMessage({ id: 'rules.TYPE' }) }],
    trigger: 'onObjectChanged',
    validateTrigger: 'onChange',
    getValueFromEvent: (value) => {
      // only for validating:
      if (value && !value.id) {
        return value;
      }
      form.setFieldsValue({
        itemTypeName: value?.description,
      });
      return value?.id;
    },
  });

  const descriptionValidator = getFieldDecorator('itemDescription', {
    initialValue: null,
    rules: [{ required: true, message: intl.formatMessage({ id: 'rules.DESCRIPTION' }) }],
  });

  const vendorSearchValue = getFieldDecorator('vendorSearchValue', {
    initialValue: null,
  });

  getFieldDecorator('costCurrency', {
    initialValue: null,
  });

  getFieldDecorator('vendorName', {
    initialValue: null,
  });

  getFieldDecorator('itemTypeName', {
    initialValue: null,
  });

  const vendorValidator = getFieldDecorator('vendor', {
    initialValue: null,
    rules: [
      {
        required: true,
        validator: (rule, value, callback) => {
          if (!value) {
            callback(intl.formatMessage({ id: 'rules.VENDOR' }));
            return;
          }

          const currency = getFieldValue('costCurrency');
          if (!currency) {
            callback(intl.formatMessage({ id: 'rules.VENDOR_MASTER' }));
            return;
          }
          callback();
        },
      },
    ],
  });

  const costValidator = getFieldDecorator('cost', {
    initialValue: null,
    rules: [{ required: true, message: intl.formatMessage({ id: 'rules.COST' }) }],
  });

  const currency = getFieldValue('costCurrency');

  return (
    <Row type='flex' gutter={16} marginBottom={10}>
      <Col span={4}>
        <FormItem label={intl.formatMessage({ id: 'order.subcontracts.TYPE' })}>
          {typeValidator(
            <ReferenceDataDropdown
              placeholder={intl.formatMessage({
                id: 'order.subcontracts.SELECT_TYPE',
              })}
              type='order'
              name='subContractItemLookup'
              valueLabel='description'
              valueKey='id'
              subType='itemTypes'
              disabled={disabled}
            />,
          )}
        </FormItem>
      </Col>
      <Col span={6}>
        <FormItem label={intl.formatMessage({ id: 'order.subcontracts.DESCRIPTION' })}>
          {descriptionValidator(
            <Input
              placeholder={intl.formatMessage({
                id: 'order.subcontracts.DESCRIPTION_PLACEHOLDER',
              })}
            />,
          )}
        </FormItem>
      </Col>
      <Col span={6} label={intl.formatMessage({ id: 'order.subcontracts.VENDOR' })}>
        <FormItem label={intl.formatMessage({ id: 'order.subcontracts.VENDOR' })}>
          {vendorValidator(
            vendorSearchValue(
              <SearchInput
                disabled={disabled}
                handleChange={onChangeAuto}
                allowClear={true}
                type='vendor'
                handleSelect={handleSelect}
                onClear={handleVendorClear}
                {...rest}
              />,
            ),
          )}
        </FormItem>
      </Col>
      <Col span={3}>
        <FormItem label={intl.formatMessage({ id: 'order.subcontracts.COST' })}>
          {costValidator(
            <StyledInputNumber
              precision={2}
              placeholder={intl.formatMessage({ id: 'order.subcontracts.COST_PLACEHOLDER' })}
              min={0.01}
              max={999999999}
              disabled={disabled || !currency}
              isVendorCurrencyChanged={isVendorCurrencyChanged}
            />,
          )}
        </FormItem>
        {isVendorCurrencyChanged &&
          <StyledChangeWarning>
            <FormattedMessage id='order.subcontracts.CURRENCY_CHANGE' />
          </StyledChangeWarning>
        }
      </Col>
      <Col span={2}>
        <FormItem label={intl.formatMessage({ id: 'order.subcontracts.CURRENCY' })}>
          <FormText>{currency}</FormText>
        </FormItem>
      </Col>
      <Col span={3}>
        <Flex marginTop={32}>
          <StyledButton
            htmlType='submit'
            type='secondary'
            disabled={disabled || isInvalid(form.getFieldsError(), form.getFieldsValue())}
            onClick={onSubmit}
          >
            <FormattedMessage id='order.subcontracts.ADD' />
          </StyledButton>
          <StyledClear onClick={() => form.resetFields()}>
            <FormattedMessage id='order.subcontracts.CLEAR' />
          </StyledClear>
        </Flex>
      </Col>
    </Row>
  );
};

SubcontractFooter.propTypes = {
  intl: PropTypes.object,
  addSubContract: PropTypes.func.isRequired,
  vendors: PropTypes.array,
  form: PropTypes.object,
  disabled: PropTypes.bool,
  setFormDirty: PropTypes.func,
};

export default Form.create()(injectIntl(SubcontractFooter));
